// Browser-compatible XML parser for WordPress exports
// No external dependencies required

export interface BlogPost {
  id: string;
  title: string;
  slug: string;
  content: string;
  excerpt: string;
  author: string;
  publishDate: Date;
  categories: string[];
  tags: string[];
  featuredImage?: string;
  images: string[];
  status: string;
  link: string;
}

export class BrowserXMLParser {
  /**
   * Parse WordPress XML export using native browser DOMParser
   */
  static async parseWordPressXML(xmlContent: string): Promise<BlogPost[]> {
    try {
      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(xmlContent, 'text/xml');
      
      // Check for parsing errors
      const errorNode = xmlDoc.querySelector('parsererror');
      if (errorNode) {
        throw new Error('XML parsing error: ' + errorNode.textContent);
      }

      // Get all item elements
      const items = xmlDoc.querySelectorAll('item');
      const posts: BlogPost[] = [];

      items.forEach((item) => {
        try {
          // Check if it's a published post
          const postType = this.getElementText(item, 'wp\\:post_type');
          const status = this.getElementText(item, 'wp\\:status');
          
          if (postType === 'post' && status === 'publish') {
            const post = this.parsePost(item);
            if (post) {
              posts.push(post);
            }
          }
        } catch (error) {
          console.warn('Error parsing individual post:', error);
        }
      });

      // Sort by date, newest first
      return posts.sort((a, b) => b.publishDate.getTime() - a.publishDate.getTime());
    } catch (error) {
      console.error('Error parsing WordPress XML:', error);
      throw error;
    }
  }

  /**
   * Parse individual post item
   */
  private static parsePost(item: Element): BlogPost | null {
    try {
      const id = this.getElementText(item, 'wp\\:post_id') || '';
      const title = this.getElementText(item, 'title') || '';
      const slug = this.getElementText(item, 'wp\\:post_name') || this.createSlug(title);
      const content = this.getElementText(item, 'content\\:encoded') || '';
      const excerpt = this.getElementText(item, 'excerpt\\:encoded') || this.getElementText(item, 'description') || '';
      const author = this.getElementText(item, 'dc\\:creator') || 'Awasi';
      const dateStr = this.getElementText(item, 'pubDate') || this.getElementText(item, 'wp\\:post_date');
      const link = this.getElementText(item, 'link') || '';

      // Parse categories and tags
      const categories = this.extractTerms(item, 'category');
      const tags = this.extractTerms(item, 'post_tag');

      // Extract images
      const images = this.extractImagesFromContent(content);
      const featuredImage = images[0]; // Use first image as featured

      // Parse date
      let publishDate: Date;
      try {
        publishDate = dateStr ? new Date(dateStr) : new Date();
      } catch {
        publishDate = new Date();
      }

      return {
        id,
        title: this.decodeHTMLEntities(title),
        slug,
        content: this.cleanContent(content),
        excerpt: this.cleanContent(excerpt) || this.createExcerpt(content),
        author,
        publishDate,
        categories,
        tags,
        featuredImage,
        images,
        status: 'publish',
        link
      };
    } catch (error) {
      console.warn('Error parsing post:', error);
      return null;
    }
  }

  /**
   * Get text content from element using CSS selector
   */
  private static getElementText(parent: Element, selector: string): string {
    const element = parent.querySelector(selector);
    return element?.textContent?.trim() || '';
  }

  /**
   * Extract categories or tags from item
   */
  private static extractTerms(item: Element, domain: string): string[] {
    const categories = item.querySelectorAll('category');
    const terms: string[] = [];

    categories.forEach((cat) => {
      const catDomain = cat.getAttribute('domain');
      if (catDomain === domain) {
        const text = cat.textContent?.trim();
        if (text) {
          terms.push(text);
        }
      }
    });

    return terms;
  }

  /**
   * Extract all image URLs from content
   */
  private static extractImagesFromContent(content: string): string[] {
    const images: string[] = [];
    
    // Create a temporary DOM element to parse HTML content
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = content;
    
    // Find all img elements
    const imgElements = tempDiv.querySelectorAll('img');
    imgElements.forEach((img) => {
      const src = img.getAttribute('src');
      if (src) {
        images.push(src);
      }
    });

    // Also use regex as fallback
    const imageRegex = /<img[^>]+src="([^"]+)"/gi;
    let match;
    while ((match = imageRegex.exec(content)) !== null) {
      if (!images.includes(match[1])) {
        images.push(match[1]);
      }
    }

    return Array.from(new Set(images)); // Remove duplicates
  }

  /**
   * Clean content from CDATA and unwanted HTML
   */
  private static cleanContent(content: string): string {
    // Remove CDATA tags if present
    content = content.replace(/<!\[CDATA\[(.*?)\]\]>/gs, '$1');
    
    // Decode HTML entities
    content = this.decodeHTMLEntities(content);
    
    // Remove WordPress shortcodes
    content = content.replace(/\[.*?\]/g, '');
    
    // Clean up excessive whitespace
    content = content.replace(/\s+/g, ' ').trim();
    
    return content;
  }

  /**
   * Create excerpt from content
   */
  private static createExcerpt(content: string, maxLength: number = 150): string {
    const cleanText = this.htmlToPlainText(content);
    return cleanText.length > maxLength 
      ? cleanText.substring(0, maxLength) + '...'
      : cleanText;
  }

  /**
   * Decode HTML entities using browser DOM
   */
  private static decodeHTMLEntities(text: string): string {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = text;
    return tempDiv.textContent || tempDiv.innerText || text;
  }

  /**
   * Create URL-friendly slug from title
   */
  private static createSlug(title: string): string {
    return title
      .toLowerCase()
      .replace(/[^\w\s-]/g, '') // Remove special characters
      .replace(/\s+/g, '-') // Replace spaces with hyphens
      .replace(/-+/g, '-') // Replace multiple hyphens with single hyphen
      .trim();
  }

  /**
   * Convert HTML content to plain text for excerpts
   */
  static htmlToPlainText(html: string): string {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;
    
    // Remove script and style elements
    tempDiv.querySelectorAll('script, style').forEach(el => el.remove());
    
    return tempDiv.textContent || tempDiv.innerText || '';
  }

  /**
   * Extract localhost image URLs that need to be migrated
   */
  static extractLocalhostImages(posts: BlogPost[]): string[] {
    const localhostImages: string[] = [];
    
    posts.forEach(post => {
      // Check featured image
      if (post.featuredImage && post.featuredImage.includes('localhost')) {
        localhostImages.push(post.featuredImage);
      }
      
      // Check content images
      post.images.forEach(image => {
        if (image.includes('localhost')) {
          localhostImages.push(image);
        }
      });
    });
    
    return Array.from(new Set(localhostImages)); // Remove duplicates
  }
}